import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  IndianRupee, TrendingUp, TrendingDown, Store, Bell, CheckCircle2, XCircle,
  AlertTriangle, Package, ShoppingCart, CalendarDays, Banknote, Smartphone, CreditCard, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip,
} from "recharts";

export default function MobileOwner() {
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState("overview"); // overview | approvals
  const [approvals, setApprovals] = useState({ purchase_orders: [], credit_sales: [], leaves: [] });
  const [nearExpiry, setNearExpiry] = useState([]);
  const [expired, setExpired] = useState([]);

  const load = async () => {
    try {
      const [o, a, ne, ex] = await Promise.all([
        api.get("/mobile/owner/overview"),
        api.get("/mobile/owner/approvals"),
        api.get("/production/batches/near-expiry?days=7").catch(() => ({ data: [] })),
        api.get("/production/batches/expired").catch(() => ({ data: [] })),
      ]);
      setOv(o.data); setApprovals(a.data);
      setNearExpiry(ne.data || []);
      setExpired(ex.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const act = async (kind, id, action, note) => {
    try {
      await api.post(`/mobile/owner/approvals/${kind}/${id}/${action}`, { note: note || null });
      toast.success(`${action === "approve" ? "Approved" : "Rejected"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!ov) return <div className="p-6 text-[#8B7355] text-sm">Loading…</div>;

  const delta = ov.delta_vs_yesterday_pct;

  return (
    <div className="pb-4" data-testid="m-owner-page">
      {/* Hero */}
      <div className="bg-[#2C1810] text-white p-5">
        <div className="uc-label !text-[#C8B8A0] mb-1">Owner monitor · Live</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-[Outfit] text-3xl font-semibold tracking-tight" data-testid="owner-today-sales">₹{Number(ov.today_sales || 0).toLocaleString("en-IN")}</span>
          {delta !== null && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${delta >= 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
              {delta >= 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> : <TrendingDown className="w-3 h-3 inline mr-0.5" />}
              {Math.abs(delta)}% vs yesterday
            </span>
          )}
        </div>
        <div className="text-xs text-[#C8B8A0] mt-1">Month to date: ₹{Number(ov.month_to_date || 0).toLocaleString("en-IN")}</div>

        {ov.trend_7d?.length > 0 && (
          <div className="mt-4 h-24 -mx-1" style={{ minHeight: 80 }} data-testid="owner-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={ov.trend_7d}>
                <XAxis dataKey="date" hide />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#3a2418", border: "1px solid #5C4033", borderRadius: 6, color: "#fff", fontSize: 11 }} formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Line type="monotone" dataKey="revenue" stroke="#F6AD55" strokeWidth={2.4} dot={{ r: 3, fill: "#C05621" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="px-4 pt-4">
        <div className="flex bg-white border border-[#EAE5D9] rounded-md p-1 text-sm">
          <button onClick={() => setTab("overview")} data-testid="owner-tab-overview" className={`flex-1 py-2 rounded-md ${tab === "overview" ? "bg-[#C05621] text-white" : "text-[#5C4033]"}`}>Overview</button>
          <button onClick={() => setTab("approvals")} data-testid="owner-tab-approvals" className={`flex-1 py-2 rounded-md flex items-center justify-center gap-1 ${tab === "approvals" ? "bg-[#C05621] text-white" : "text-[#5C4033]"}`}>
            Approvals {ov.approvals?.total > 0 && <span className={`text-[10px] px-1.5 rounded-full ${tab === "approvals" ? "bg-white/30" : "bg-red-500 text-white"}`}>{ov.approvals.total}</span>}
          </button>
        </div>
      </div>

      {tab === "overview" && (
        <div className="p-4 space-y-4">
          {/* Collections */}
          <Card title="Today's Collections">
            <div className="grid grid-cols-2 gap-2">
              <Metric icon={Banknote} label="Cash" value={ov.collections_today.cash} accent="text-emerald-700 bg-emerald-50" testid="owner-cash" />
              <Metric icon={Smartphone} label="UPI/GPay" value={ov.collections_today.upi} accent="text-purple-700 bg-purple-50" testid="owner-upi" />
              <Metric icon={CreditCard} label="Card" value={ov.collections_today.card} accent="text-slate-700 bg-slate-100" testid="owner-card" />
              <Metric icon={AlertTriangle} label="Credit" value={ov.collections_today.credit} accent="text-red-700 bg-red-50" testid="owner-credit" />
            </div>
          </Card>

          {/* Branches */}
          <Card title="Branch performance today">
            <ul className="space-y-1.5">
              {ov.branches?.filter((b) => b.todays_sales > 0).slice(0, 6).map((b) => (
                <li key={b.branch_id} className="flex items-center justify-between p-3 rounded-md border border-[#EAE5D9]" data-testid={`owner-branch-${b.branch_id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-[#FEEBC8] text-[#C05621] flex items-center justify-center"><Store className="w-4 h-4" /></div>
                    <div>
                      <div className="font-medium text-sm">{b.branch_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">{b.branch_code} · {b.txns} txn</div>
                    </div>
                  </div>
                  <div className="font-semibold">₹{Math.round(b.todays_sales).toLocaleString("en-IN")}</div>
                </li>
              ))}
              {ov.branches?.filter((b) => b.todays_sales > 0).length === 0 && (
                <li className="text-center py-6 text-[#8B7355] text-sm">No branch sales yet today.</li>
              )}
            </ul>
          </Card>

          {/* Alerts */}
          <Card title="Alerts">
            <div className="flex items-center gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
              <Package className="w-5 h-5 text-amber-700 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-medium text-amber-900">Low stock SKUs</div>
                <div className="text-xs text-amber-800">{ov.alerts.low_stock_skus} product{ov.alerts.low_stock_skus === 1 ? "" : "s"} across branches at or below threshold</div>
              </div>
              <div className="font-[Outfit] text-2xl font-semibold text-amber-900">{ov.alerts.low_stock_skus}</div>
            </div>
            {expired.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-md bg-red-50 border border-red-200 mt-2" data-testid="owner-expired-alert">
                <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-red-900">Expired batches</div>
                  <div className="text-xs text-red-800 mt-0.5">{expired.length} batch{expired.length === 1 ? "" : "es"} past expiry — POS will block sales automatically.</div>
                  <ul className="text-[11px] text-red-800 mt-1 space-y-0.5">
                    {expired.slice(0, 3).map((b) => (
                      <li key={b.id}><b>{b.batch_no}</b> · {b.product_name} · expired {b.expiry_date}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {nearExpiry.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-200 mt-2" data-testid="owner-near-expiry-alert">
                <Package className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-amber-900">Near-expiry (next 7 days)</div>
                  <ul className="text-[11px] text-amber-800 mt-1 space-y-0.5">
                    {nearExpiry.slice(0, 4).map((b) => (
                      <li key={b.id}><b>{b.batch_no}</b> · {b.product_name} · expires {b.expiry_date}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "approvals" && (
        <div className="p-4 space-y-4">
          <Card title={`Pending purchase orders (${approvals.purchase_orders.length})`} icon={ShoppingCart}>
            {approvals.purchase_orders.length === 0 ? (
              <div className="text-center py-4 text-sm text-[#8B7355]">All caught up.</div>
            ) : approvals.purchase_orders.map((po) => (
              <ApprovalRow
                key={po.id} testid={`owner-po-${po.id}`}
                title={po.number}
                subtitle={`${po.supplier?.name} · ₹${Math.round(po.grand_total || 0).toLocaleString("en-IN")}`}
                onApprove={() => act("po", po.id, "approve")}
                onReject={() => act("po", po.id, "reject")}
              />
            ))}
          </Card>

          <Card title={`Credit sales pending (${approvals.credit_sales.length})`} icon={AlertTriangle}>
            {approvals.credit_sales.length === 0 ? (
              <div className="text-center py-4 text-sm text-[#8B7355]">No outstanding credits.</div>
            ) : approvals.credit_sales.slice(0, 10).map((s) => (
              <div key={s.id} className="p-3 rounded-md border border-[#EAE5D9] flex items-center justify-between" data-testid={`owner-credit-${s.id}`}>
                <div>
                  <div className="font-medium text-sm">{s.number}</div>
                  <div className="text-xs text-[#8B7355]">{s.customer_snapshot?.name} · {s.branch_code}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-sm">₹{Math.round(s.grand_total || 0).toLocaleString("en-IN")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-red-600">{s.payment_status}</div>
                </div>
              </div>
            ))}
          </Card>

          <Card title={`Leave requests (${approvals.leaves.length})`} icon={CalendarDays}>
            {approvals.leaves.length === 0 ? (
              <div className="text-center py-4 text-sm text-[#8B7355]">Nothing pending.</div>
            ) : approvals.leaves.map((l) => (
              <ApprovalRow
                key={l.id} testid={`owner-leave-${l.id}`}
                title={l.leave_type ? `${l.leave_type} leave` : "Leave"}
                subtitle={`${l.employee_name || l.employee_code || "—"} · ${l.from_date || ""} → ${l.to_date || ""}`}
                onApprove={() => act("leave", l.id, "approve")}
                onReject={() => act("leave", l.id, "reject")}
              />
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-[#C05621]" />}
        <div className="uc-label">{title}</div>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="p-3 rounded-md border border-[#EAE5D9]" data-testid={testid}>
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-full ${accent} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">{label}</div>
      </div>
      <div className="font-[Outfit] text-lg font-semibold mt-1 tracking-tight">₹{Math.round(Number(value || 0)).toLocaleString("en-IN")}</div>
    </div>
  );
}

function ApprovalRow({ title, subtitle, onApprove, onReject, testid }) {
  return (
    <div className="p-3 rounded-md border border-[#EAE5D9]" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{title}</div>
          <div className="text-xs text-[#8B7355] truncate">{subtitle}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onReject} data-testid={`${testid}-reject`} className="w-8 h-8 rounded-md border border-red-200 text-red-500 flex items-center justify-center hover:bg-red-50"><XCircle className="w-4 h-4" /></button>
          <button onClick={onApprove} data-testid={`${testid}-approve`} className="w-8 h-8 rounded-md bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
